import { supabase } from '../lib/supabase';
import { addCsrfHeader } from '../utils/csrf';
import { handleRecaptchaError, type RecaptchaError } from '../utils/recaptcha';

export async function verifyReCaptcha(token: string, action: string): Promise<boolean> {
  try {
    // Skip verification in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('Skipping reCAPTCHA verification in development');
      return true;
    }

    // Check if reCAPTCHA is enabled in config
    const { data: configData } = await supabase
      .from('config')
      .select('value')
      .eq('key', 'recaptcha_enabled')
      .maybeSingle();

    // If disabled in config, skip verification
    if (configData && configData.value === 'false') {
      console.log('reCAPTCHA verification bypassed - disabled in config');
      return true;
    }

    if (!token) {
      console.error('No reCAPTCHA token provided');
      return false;
    }

    // Get base URL from environment
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!baseUrl) {
      throw new Error('VITE_SUPABASE_URL is not configured');
    }

    // Ensure base URL has trailing slash
    const normalizedBaseUrl = baseUrl.endsWith('/')
      ? baseUrl
      : `${baseUrl}/`;

    // Construct verification URL
    const verifyUrl = `${normalizedBaseUrl}functions/v1/verify-recaptcha`;

    // Log verification attempt
    console.log('ReCAPTCHA verification:', {
      action,
      verifyUrl,
      tokenLength: token?.length ?? 0
    });

    // Make verification request
    const response = await fetch(verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        ...addCsrfHeader({})
      },
      body: JSON.stringify({ 
        token: encodeURIComponent(token), 
        action: encodeURIComponent(action) 
      })
    });

    // Handle non-200 responses
    if (!response.ok) {
      const errorText = await response.text();
      let errorJson;
      try {
        errorJson = JSON.parse(errorText);
      } catch (e) {
        errorJson = { error: errorText };
      }
      
      console.error('ReCAPTCHA verification failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorJson
      });

      throw new Error('ReCAPTCHA verification failed', {
        cause: {
          status: response.status,
          error: errorJson
        }
      });
    }

    const result = await response.json();
    console.log('ReCAPTCHA verification result:', result);

    if (!result.success) {
      console.error('ReCAPTCHA verification unsuccessful:', result);
      return false;
    }

    // Verify action matches if provided
    if (action && result.action !== action) {
      console.error('ReCAPTCHA action mismatch:', {
        expected: action,
        received: result.action
      });
      return false;
    }

    // Check score if provided
    if (typeof result.score === 'number' && result.score < 0.3) {
      console.error('ReCAPTCHA score too low:', result.score);
      return false;
    }

    return true;
  } catch (error) {
    console.error('ReCAPTCHA verification error:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      token: token ? 'present' : 'missing',
      action
    });

    throw handleRecaptchaError(error);
  }
}